import { useCallback, useEffect, useState } from 'react';

export interface Rotator {
  readonly index: number;
  /** Nhảy tới pane `i` và đặt lại đồng hồ. Chỉ số ngoài dải được chuẩn hoá vào `[0, count)`. */
  readonly goTo: (i: number) => void;
  /** Dừng/chạy tiếp — dùng cho hover và focus (WCAG 2.2.2). */
  readonly setPaused: (paused: boolean) => void;
}

/**
 * Media query của "giảm chuyển động". Khai một chỗ vì hook ĐỌC nó rồi còn ĐĂNG KÝ theo nó — gõ
 * lệch một ký tự giữa hai chỗ thì đăng ký im lặng không bao giờ khớp.
 */
const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Tự đổi pane theo chu kỳ — bản React của `startHero()` trong thiết kế (template.html dòng
 * 1396–1399: `setInterval(… (heroIdx + 1) % 3 …, 3600)`).
 *
 * Thiết kế chạy `setInterval` vô điều kiện và 3 dot chỉ là `<span>` không bấm được. Ba thứ phải
 * sửa (design doc §6.2), và cả ba đều là HÀNH VI — hình thức không đổi một pixel:
 *
 *   1. `prefers-reduced-motion: reduce` ⇒ không tự chạy.
 *   2. Tab ẩn ⇒ dừng. Không đốt pin và không re-render khi không ai xem.
 *   3. Có cách dừng và điều khiển ⇒ đạt WCAG 2.2.2. Phía gọi biến 3 dot thành `<button>` và
 *      gọi `setPaused` khi hover/focus.
 *
 * `goTo` đặt lại đồng hồ: bấm chọn pane rồi bị đổi sau 200ms là cảm giác hỏng.
 *
 * Khác `revealAll()` ở một điểm quan trọng: hook này THEO DÕI hai điều kiện môi trường bằng
 * event chứ không đọc một lần lúc gắn. `revealAll()` được phép đọc một lần vì lớp canh thật của
 * nó là `@media (prefers-reduced-motion: reduce)` trong `motion.css`; ở đây chuyển động là state
 * React nên CSS không đỡ được gì, không theo dõi là bật "giảm chuyển động" giữa chừng không có
 * tác dụng cho tới khi tải lại trang.
 */
export function useRotator(count: number, intervalMs = 3600): Rotator {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [reduced, setReduced] = useState(false);
  // Tăng lên mỗi lần `goTo` để effect dựng lại `setInterval` từ mốc thời gian mới. Nó là TÍN
  // HIỆU chứ không phải dữ liệu, nên effect không đọc giá trị — chỉ deps cần thấy nó đổi.
  const [restarts, setRestarts] = useState(0);

  useEffect(() => {
    const sync = () => setHidden(document.visibilityState === 'hidden');
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia(REDUCE_QUERY);
    const sync = () => setReduced(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => {
      query.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    // `count < 2` gồm cả 0 và 1: không có gì để xoay thì đừng dựng đồng hồ nào cả.
    if (count < 2 || paused || hidden || reduced) return;

    const timer = setInterval(() => {
      setIndex((value) => (value + 1) % count);
    }, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [count, intervalMs, paused, hidden, reduced, restarts]);

  const goTo = useCallback(
    (next: number) => {
      setIndex(count > 0 ? ((next % count) + count) % count : 0);
      setRestarts((value) => value + 1);
    },
    [count],
  );

  // Chuẩn hoá lúc ĐỌC, không chỉ lúc ghi: `count` có thể giảm sau khi `index` đã nhảy xa (phía
  // gọi đổi số pane theo khổ màn hình), và khi đó `panes[index]` là `undefined`.
  return { index: count > 0 ? index % count : 0, goTo, setPaused };
}
